import NavShell from '../components/NavShell';

export default function TermsPage() {
  return (
    <NavShell>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <h1 className="text-3xl font-bold text-[#111827]">Terms &amp; Conditions</h1>
        <p className="text-sm text-gray-500">Last updated: April 11, 2026</p>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">1. Eligibility</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Swipe Opinion is available to individuals who are at least 18 years of age and reside in
            the United States. By creating an account, you represent and warrant that you meet these
            eligibility requirements. We reserve the right to verify your identity and eligibility at
            any time and to suspend or terminate accounts that do not meet these requirements.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">2. How Points Are Earned</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Members earn points by completing surveys, onboarding tasks, and extended profiler modules.
            Points are awarded only after a survey provider confirms completion via a server-to-server
            postback. The number of points awarded varies by survey and is displayed before you begin.
            Swipe Opinion reserves the right to adjust point values at any time.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">3. Points Redemption</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Points may be redeemed for gift cards, PayPal payouts, or bank transfers once you reach
            the minimum redemption threshold of 500 points ($5.00). Points have no cash value until
            redeemed through an available redemption method. Redemption requests are processed within
            1-3 business days. Once a redemption is marked as processing, it cannot be reversed.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">4. Points Expiration</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Points expire after 6 months (180 days) of account inactivity. An account is considered
            inactive if you have not completed a survey or logged in within the preceding 180 days.
            Before expiration, we will send reminder emails at 7 days, 3 days, and 1 day before
            expiration. If your account becomes inactive and points are forfeited, they may be
            reinstated if you return and reactivate your account, except in cases of suspension
            for fraud or abuse.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">5. Prohibited Activities</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            You agree not to engage in any of the following:
          </p>
          <ul className="list-disc list-inside text-sm text-[#111827]/80 space-y-1 ml-2">
            <li>Creating multiple accounts</li>
            <li>Providing false or misleading profile information</li>
            <li>Using bots, scripts, or automated tools to complete surveys</li>
            <li>Attempting to manipulate or game the points system</li>
            <li>Sharing your account credentials with others</li>
            <li>Engaging in any form of fraud or deceptive activity</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">6. Account Termination</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We reserve the right to suspend or terminate your account at any time if we detect
            fraudulent activity, violation of these terms, or behavior that negatively impacts the
            platform or other members. Members with a fraud score of 80 or above will be automatically
            suspended. Suspended accounts forfeit all accumulated points and are not eligible for
            reinstatement. You may voluntarily close your account at any time by contacting support.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">7. Survey Disqualification</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            You may be disqualified from a survey at any time if you do not meet the survey's
            targeting criteria or if the survey's quota has been filled. Disqualification is determined
            by the survey provider. In some cases, consolation points may be awarded for disqualifications,
            subject to minimum time requirements and daily caps.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">8. Limitation of Liability</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            Swipe Opinion is provided "as is" without warranties of any kind, either express or
            implied. We are not liable for any indirect, incidental, special, consequential, or
            punitive damages arising from your use of the platform, including but not limited to
            loss of points, inability to redeem, or interruption of service. Our total liability
            shall not exceed the dollar value of points in your account at the time of the claim.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">9. Governing Law</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the State
            of Delaware, without regard to its conflict of law provisions. Any disputes arising from
            or relating to these Terms or your use of the platform shall be resolved exclusively in
            the state or federal courts located in Delaware.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">10. Changes to Terms</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            We may update these Terms from time to time. When we do, we will revise the "Last updated"
            date at the top of this page and notify you via email or in-app notification. Your continued
            use of Swipe Opinion after any changes constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-[#111827]">11. SMS and Push Notifications</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            By providing your phone number and opting in, you consent to receive SMS messages from
            Swipe Opinion including survey alerts and account notifications. Message frequency varies.
            Message and data rates may apply. Reply STOP to unsubscribe, HELP for help. You may also
            enable web push notifications through your browser. You can manage all notification
            preferences in your profile settings at any time.
          </p>
        </section>

        <section className="space-y-3 pb-8">
          <h2 className="text-xl font-semibold text-[#111827]">12. Contact</h2>
          <p className="text-sm text-[#111827]/80 leading-relaxed">
            If you have questions about these Terms, please contact us at{' '}
            <a href="mailto:support@swipeopinion.com" className="text-[#5B4FE8] hover:underline">
              support@swipeopinion.com
            </a>.
          </p>
        </section>
      </main>
    </NavShell>
  );
}
